import Vue from 'vue';
import SelectionLocationHelper from '../../utils/selection-location-helper';
import Curriculum from 'client/js/models/curriculum.js'
import moment from 'moment';


function getNewStateObj() {
   return {
      filter: 'all',
      searchString: '',
      course: null,
      courseUpdates: {},
      incDrafts: false
   }
}

const getters = {
   // returns the selected course
   getSelectedCourse: state => state.course,

   // returns the stored search course parameters
   getSearchCourseParams: state => {
      return {
         searchString: state.searchString,
         filter: state.filter
      };
   },

   isUnpublished: state => !state.course?.isWhitelisted || !state.course?.isActive,
   isDraft: state => state.course?.status === 'Draft',
   includeDraftVersions: state => state.course?.status === 'Draft' || state.incDrafts,
   includeDraftVersionSetting: state => state.incDrafts,
   // Require authentication if the course is a draft or if we are including drafts
   requiresAuth: state => state.course?.status === 'Draft'  || state.incDrafts,
   // Doesn't require authentication if the course is not a draft and we are not including drafts
   isPublic: state => state.course?.status != 'Draft' && !state.incDrafts
}

const mutations = {

   // set selected course
   setSelectedCourse(state, course) {
      state.course = course;
   },

   // set course and incDrafts
   setSelectedCourseAndDrafts(state, {course, incDrafts}) {
      state.course = course;
      state.incDrafts = incDrafts;
   },

   // set incDrafts
   setIncludeDrafts(state, incDrafts) {
      state.incDrafts = incDrafts;
   },

   // set search source parameters so we can replay the request if required
   setSearchCourseParams(state, {searchString, filter}) {
      state.searchString = searchString;
      state.filter = filter;
   },

   resetState: (state) => {
      Object.assign(state, getNewStateObj())
   }
}

const actions = {
   async validateAndLoadCourse({ commit }, { courseId, courseLoopVersion, incDrafts }) {
      // console.log('validateAndLoadCourse', courseId, courseLoopVersion, incDrafts)
      const loadedCourse = await Curriculum.getCourse(courseId, courseLoopVersion, incDrafts)
      if (loadedCourse == null) {
         throw new Error("Course Not Found");
      } else if (moment(loadedCourse.endDate).isBefore(moment())) {
         throw new Error("Course no longer offered")
      }
      commit('setSelectedCourse', loadedCourse);
   },

   // attach structure to selected course
   attachStructure({ commit, getters }, { location, structure }) {
      const selectedCourse = getters['getSelectedCourse'];

      // traverse location until we get the exact place
      let item = null;
      selectedCourse.structure[0].containers.some(c => {
         item = SelectionLocationHelper.findLocation('root', c, location)
         return item;
      })

      // attach structure if item is found
      if (item) {
         Vue.set(item, 'structure', structure)
         commit('setSelectedCourse', selectedCourse);
      }
   },
   // detach structure from selected course
   detachStructure({ commit, getters, dispatch }, location) {
      const selectedCourse = getters['getSelectedCourse'];

      // traverse location until we get the exact place
      let item = null;
      selectedCourse.structure[0].containers.some(c => {
         item = SelectionLocationHelper.findLocation('root', c, location)
         return item;
      })

      // remove structure if item is found
      if (item) {
         Vue.set(item, 'structure', null)
         commit('setSelectedCourse', selectedCourse)

         if (item.relationship && item.relationship.length) {
            item.relationship.forEach(rel => {
               const relationshipLocation = SelectionLocationHelper.getRelationshipLocation(location, rel)
               dispatch('detachStructure', relationshipLocation)
            })
         }
      }
   },
   // add relationships
   addRelationships({ commit, getters }, { location, relationships }) {
      const selectedCourse = getters['getSelectedCourse'];
      // traverse location until we get the exact place
      let item = null;
      selectedCourse.structure[0].containers.some(c => {
         item = SelectionLocationHelper.findLocation('root', c, location)
         return item;
      })

      // append new relationships if item is found
      if (item) {
         const existingRelationshipIds = item.relationship.map(rel => rel.id);
         const newRelationships = relationships.filter(rel => {
            return existingRelationshipIds.indexOf(rel.id) === -1
         }).map(r => {
            return {
               ...r,
               virtual: true
            }
         })

         if (newRelationships.length) {
            Vue.set(item, 'relationship', [...item.relationship, ...newRelationships])
            commit('setSelectedCourse', selectedCourse);
         }
      }
   },
   // remove all relationships from location
   removeRelationships({ commit, getters }, { location, relationships }) {
      const selectedCourse = getters['getSelectedCourse'];

      // traverse location until we get the exact place
      let item = null;
      selectedCourse.structure[0].containers.some(c => {
         item = SelectionLocationHelper.findLocation('root', c, location)
         return item;
      })

      // remove relationships if item is found
      if (item) {
         const removedRelationshipIds = relationships.map(rel => rel.id)

         // get relationships that is not in the list of ids to be removed
         const updatedRelationships = item.relationship
            .slice()
            .filter(rel => {
               return removedRelationshipIds.indexOf(rel.id) === -1
            });

         if (updatedRelationships.length !== item.relationship.length) {
            Vue.set(item, 'relationship', updatedRelationships)
            commit('setSelectedCourse', selectedCourse);
         }
      }
   }
}

export default {
   namespaced: true,
   state: getNewStateObj(),
   getters,
   actions,
   mutations,
   getNewStateObj
}
